import { ChangeEvent, Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import {
    CallInfo,
    CallReason,
    DecryptedCallInfo,
    FormFieldInterface,
    SourceInterface,
} from 'src/api';
import { LangPicker } from 'src/components/lang-picker';
import { fitToMask } from 'src/utils/helpers';
import { ReasonsCard } from '../reason-card/index';
import { BackIcon } from './back-icon';
import './form.scss';
import { validateBIN, validateIIN, validatePhone } from './model';
import { CloseIcon } from '../intro-form/close-icon';
import AgreementField from './agreement-field';
import { getLogo } from 'src/themes/useLogo';
import qr from 'qrcode';
const patterns = {
    X: /[A-Za-z]/,
    9: /[0-9]/,
};

type ValidationError = {
    [k: string]: 'requiredError' | 'notValid' | 'genericError';
};

interface ParticipantInfoSourceFormProps extends WithTranslation {
    callInfo?: CallInfo;
    error?: boolean;
    errorCode?: string;
    data?: DecryptedCallInfo;
    onDataFilled: (data: DecryptedCallInfo) => void;
    showOperatorCode: boolean;
    reasons?: CallReason[];
    onGoBack?: () => void;
    canGoBack?: boolean;
    onClose?: () => void;
    source: SourceInterface;
    openFileViewer?: (url: string) => void,
}

interface ParticipantInfoSourceFormState {
    reason: number | null;
    operator: string;
    errors: ValidationError;
    fieldList: FormFieldInterface[];
    showCallReason: boolean;
    filteredReasons: CallReason[];
    qr?: string;
}

class ParticipantInfoSourceForm extends Component<
    ParticipantInfoSourceFormProps,
    ParticipantInfoSourceFormState
> {
    private forLegalEntities: boolean;
    private forMarket: boolean;
    private forBcc: boolean;
    private forOtbasy: boolean;

    constructor(props: ParticipantInfoSourceFormProps) {
        super(props);
        const interfaceType = window.VBwidgetConfig.interfaceType;
        this.forLegalEntities = interfaceType === 'legal-entity';
        this.forMarket = interfaceType === 'market';
        this.forBcc = interfaceType === 'bcc';
        this.forOtbasy = interfaceType === 'otbasy';
        this.state = {
            reason: null,
            operator: '',
            errors: {},
            fieldList: [],
            showCallReason: true,
            filteredReasons: [],
            qr: '',
        };
    }

    public componentDidMount() {
        const { source, reasons, i18n } = this.props;
        this.setQR();
        const sourceFields = [...source.fieldList];
        const formattedFields: FormFieldInterface[] = sourceFields.map(
            (field) => {
                let pattern = '';
                let mask = '';

                if (field.key === 'phone') {
                    mask = '+7 (999) 999 99 99';
                    pattern =
                        /\+7 \([0-9]{3}\) [0-9]{3} [0-9]{2} [0-9]{2}$/.toString();
                }

                if (field.key === 'iin' || field.key === 'bin') {
                    mask = '999999999999';
                    pattern = /[0-9]+$/.toString();
                }

                if (field.key === 'reason') {
                    const activeReasonsList = field.settings.list.filter(
                        (field) => field.isActive
                    );
                    const checkedReasonIndex = activeReasonsList.findIndex(
                        (item) => item.selected
                    );
                    const checkedReasonCode =
                        activeReasonsList[checkedReasonIndex].code;

                    const filteredReasons: CallReason[] = [];
                    activeReasonsList.forEach((item) => {
                        const reasonData = reasons.find(
                            (reason) => reason.code === item.code
                        );
                        if (reasonData) {
                            filteredReasons.push(reasonData);
                        }
                    });

                    this.setState({
                        reason: checkedReasonCode,
                        showCallReason: filteredReasons.length > 1,
                        filteredReasons: filteredReasons
                    });
                }

                // Язык мог смениться до монтирования формы (например, на IntroForm)
                if (field.key === 'lang') {
                    return {
                        ...field,
                        mask,
                        pattern,
                        settings: {
                            ...field.settings,
                            value: i18n.language,
                        },
                    };
                }

                return {
                    ...field,
                    mask,
                    pattern,
                };
            }
        );

        this.setFormValuesFromUrl(formattedFields);
    }

    private setQR = () => {
        qr.toDataURL(window.location.href).then((value) => {
            this.setState({ qr: value });
        });
    }

    private setFormValuesFromUrl = (fields: FormFieldInterface[]) => {
        const paramString = window.location.href.split('?')[1] || '';
        const urlParams = new URLSearchParams(paramString);

        if (urlParams.size === 0) {
            this.setState({ fieldList: [...fields] });
            return;
        }
        const stateChanges: Partial<ParticipantInfoSourceFormState> = {};

        const formattedFields: FormFieldInterface[] = [...fields];
        urlParams.forEach((paramValue, paramKey) => {
            if (paramValue && paramKey === 'operator') {
                stateChanges.operator = String(paramValue);
            }

            const fieldIndex = formattedFields.findIndex((field) => {
                const isDynamicField = field.key === 'dynamic';
                const key = isDynamicField ? field.settings.paramName : field.key;
                return key === paramKey;
            });

            if (fieldIndex === -1 || paramValue === undefined) return;
            const fieldToEdit = formattedFields[fieldIndex];

            // Меняем активную тему обращения
            if (paramKey === 'reason') {
                const fieldReasons = fieldToEdit.settings.list;
                const reasonIndex = fieldReasons.findIndex(
                    (item) => item.code === Number(paramValue)
                );

                if (reasonIndex !== -1) {
                    fieldReasons.forEach((item) => {
                        item.selected = item.code === Number(paramValue);
                    });
                    stateChanges.reason = Number(paramValue);
                }
            }

            // Меняем язык
            if (paramKey === 'lang' && ['ru', 'kz', 'en'].includes(paramValue)) {
                this.props.i18n.changeLanguage(paramValue);
            }

            // Подводим значение под маску
            if (fieldToEdit.mask) {
                fieldToEdit.settings = {
                    ...fieldToEdit.settings,
                    value: fitToMask(paramValue.toString(), fieldToEdit.mask, patterns),
                };
            }

            // Задаем значение по умолчанию для остальных видов полей
            fieldToEdit.settings = {
                ...fieldToEdit.settings,
                value: paramValue.toString(),
            };
        });
        stateChanges.fieldList = [...formattedFields];

        this.setState(prevState => ({
            ...prevState,
            ...stateChanges
        }));
    };

    private validateForm = () => {
        const { fieldList } = this.state;
        const errors: ValidationError = {};

        fieldList.forEach((field, index) => {
            let key = field.key;
            if (key === 'dynamic') {
                key = field.settings.paramName;
            }

            const isSelect =
                field.settings && field.settings.list !== undefined;

            if (key === 'agreement' && field.isRequired && !field.settings.agreementChecked) {
                const indexedKey = `agreement${index}`;
                errors[indexedKey] = 'requiredError';
            }

            if (isSelect) {
                const activeItemIndex = field.settings.list.findIndex(
                    (item) => item.selected
                );

                if (activeItemIndex === -1 && field.isRequired) {
                    errors[key] = 'requiredError';
                }
            }

            if (!isSelect && key !== 'agreement') {
                let value = field.settings ? field.settings.value : '';
                value = value ? value : '';
                const isPhone = key === 'phone';
                const normalizedValue =
                    isPhone
                        ? value.trim().replace(/[^0-9+]/g, '')
                        : value.trim();

                if (normalizedValue === '' && field.isRequired) {
                    errors[key] = 'requiredError';
                }
                if (
                    normalizedValue !== '' &&
                    key === 'phone' &&
                    !validatePhone(normalizedValue) &&
                    field.isRequired
                ) {
                    errors[key] = 'notValid';
                }

                if (
                    normalizedValue !== '' &&
                    key === 'iin' &&
                    !validateIIN(normalizedValue) &&
                    field.isRequired
                ) {
                    errors[key] = 'notValid';
                }

                if (
                    normalizedValue !== '' &&
                    key === 'bin' &&
                    !validateBIN(normalizedValue) &&
                    field.isRequired
                ) {
                    errors[key] = 'notValid';
                }

                if (
                    value === '' &&
                    (key === 'name' || key === 'surname') &&
                    field.isRequired
                ) {
                    errors[key] =
                        errors[key] && errors[key] === 'requiredError'
                            ? 'requiredError'
                            : 'notValid';
                }
            }
        });

        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return false;
        }

        return true;
    };

    private registerCall = () => {
        const isValid = this.validateForm();
        if (!isValid) {
            return;
        }

        const { reason, operator, fieldList } = this.state;

        const filteredFields = fieldList.filter(
            (field) => field.key !== 'reason'
        );
        const fieldObject: { [key: string]: string | number | boolean | undefined } = {};

        for (const field of filteredFields) {
            const { key, settings } = field;
            const value = settings?.value || '';
            const normalizedValue = value.trim();

            if (key === 'dynamic') {
                const paramName = settings.paramName;
                fieldObject[paramName] = normalizedValue;
                continue;
            }

            if (key === 'phone') {
                fieldObject[key] = normalizedValue.replace(/[^0-9+]/g, '');
                continue;
            }

            if (key === 'agreement') {
                fieldObject[key] = settings.agreementChecked;
                continue;
            }

            fieldObject[key] = normalizedValue;
        }

        this.props.onDataFilled({
            ...fieldObject,
            reason,
            operator,
        });
    };

    private clearLatin = (value: string) => {
        if (window.VBwidgetConfig.formSettings.isBlockLatin) {
            return value.replace(/[A-Za-z]/g, '');
        }
        return value;
    };

    private onInputChange = (event: ChangeEvent<HTMLInputElement>, index = 0) => {
        const target = event.target;
        const { name, value, checked } = target;
        const { fieldList } = this.state;

        const sanitizedValue = this.clearLatin(value);

        const editedFields: FormFieldInterface[] = fieldList.map((field, fieldindex) => {
            if (field.key === name && name === 'phone' && value === '') {
                return {
                    ...field,
                    settings: {
                        value: '+7 (',
                    },
                };
            }

            const indexedKey = field.key + index;
            const indexedAgreement = `agreement${index}`;
            if (indexedKey === name && name === indexedAgreement && fieldindex === index) {
                return {
                    ...field,
                    settings: {
                        ...field.settings,
                        agreementChecked: checked,
                    },
                };
            }

            if (field.key === 'dynamic') {
                const paramName = field.settings.paramName;
                if (paramName === name) {
                    return {
                        ...field,
                        settings: {
                            ...field.settings,
                            value: sanitizedValue,
                        },
                    };
                }
            }

            if (field.key === name) {
                return {
                    ...field,
                    settings: {
                        ...field.settings,
                        value: sanitizedValue,
                    },
                };
            }

            return {
                ...field,
            };
        });

        this.setState({ errors: {}, fieldList: [...editedFields] });
    };

    private getReason = (callInfo?: CallInfo) => {
        const { i18n, t, source } = this.props;
        const { showCallReason } = this.state;
        const video = t('title');
        const sip = t('sip-title');
        const localCall = source.callType == 'sip' ? sip : video;

        if (!callInfo || showCallReason) {
            return localCall;
        }
        if (!callInfo || showCallReason) {
            return localCall;
        }

        if (!callInfo) {
            return '';
        }

        const lang = i18n.language;
        if (lang === 'kz') {
            return callInfo.REASON_KZ;
        }

        return callInfo.REASON_RU;
    };

    private handleReasonChange = (value: number) => {
        this.setState({ reason: value, errors: {} });
    };

    private setLanguage = (event: ChangeEvent<HTMLSelectElement>) => {
        const lang = event.target.value;
        this.props.i18n.changeLanguage(lang);
    };

    private updateLangFieldValue = (lang: string) => {
        this.setState((prevState) => {
            const updatedFieldList = prevState.fieldList.map((field) => {
                if (field.key === 'lang') {
                    return {
                        ...field,
                        settings: {
                            ...field.settings,
                            value: lang,
                        },
                    };
                }
                return field;
            });

            return { fieldList: updatedFieldList };
        });
    };

    private getFieldLabel = (name: string, labels: {
        ru: string,
        kz: string,
        en: string
    }) => {
        const { i18n, t } = this.props;
        const curLang = i18n.language as 'ru' | 'kz' | 'en';
        const label = labels[curLang] || t(`form:${name}:label`);
        return label;
    }


    private headerBtn = () => {
        const { source, onGoBack, onClose, canGoBack, t } = this.props;
        if ((source.showFirstPage && onGoBack) || canGoBack) {
            return <div
                className='vb-back-icon-container'
                onClick={onGoBack}
            >
                <BackIcon />
                <span>{t('back')}</span>
            </div>;
        }

        if (!source.showFirstPage && onClose) {
            return <div className='vb-close-icon-container' onClick={onClose}>
                <CloseIcon />
            </div>;
        }

        return <div></div>;
    }

    render() {
        const {
            t,
            callInfo,
            error,
            errorCode,
            showOperatorCode,
            openFileViewer
        } = this.props;
        const { errors, reason, operator, fieldList, showCallReason, filteredReasons, qr } =
            this.state;
        const logo = getLogo();
        const headerBtn = this.headerBtn();
        return (
            <div className='vb-form-container vb-pi-card'>
                <LangPicker
                    className='vb-vb-form-lang-picker'
                    onChange={this.updateLangFieldValue}
                />
                <div>
                    <div className='vb-pi-card__header'>
                        <div className='vb-bccIcon'>
                            {logo && <img src={logo} alt="logo" />}
                        </div>
                        <div className='vb-back-block'>
                            {headerBtn}
                            <header>{this.getReason(callInfo)}</header>
                        </div>
                    </div>
                    <form onSubmit={this.validateForm}>
                        {fieldList.map((field, index) => {
                            if (field.key === 'lang') return null;

                            if (field.key === 'dynamic') {
                                const label = this.getFieldLabel(field.key, {
                                    ru: field.labelRu,
                                    kz: field.labelKz,
                                    en: field.labelEn,
                                });

                                if (errors[field.settings.paramName] && field.isVisible === false) {
                                    return (<div className='vb-ms-3 vb-mt-1 vb-text-danger'>
                                        {t(
                                            `form:dynamic:${errors[field.settings.paramName]}`,
                                            { key: label }
                                        )}
                                    </div>);
                                }

                                if (field.isVisible === false) {
                                    return null;
                                }

                                return (
                                    <div
                                        className='vb-mb-2 vb-form-floating'
                                        key={field.settings.paramName}
                                    >
                                        <input
                                            autoComplete="on"
                                            name={field.settings.paramName}
                                            placeholder={t(
                                                'form:dynamic:placeholder',
                                                { key: label }
                                            )}
                                            value={
                                                field.settings &&
                                                    field.settings.value
                                                    ? field.settings.value
                                                    : ''
                                            }
                                            inputMode="text"
                                            onChange={this.onInputChange}
                                            className={`vb-form-control ${errors[field.settings.paramName] ? 'vb-is-invalid' : ''}`}
                                            required={field.isRequired}
                                        />
                                        <label className='vb-form-label'>
                                            <span className='vb-floating-text'>
                                                {label}
                                                {field.isRequired && '*'}
                                            </span>
                                        </label>
                                        {errors[field.settings.paramName] && (
                                            <div className='vb-ms-3 vb-mt-1 vb-text-danger'>
                                                {t(
                                                    `form:dynamic:${errors[field.settings.paramName]}`,
                                                    { key: label }
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (field.key === 'reason' && showCallReason) {
                                return (
                                    <div key={field.key} className='vb-mb-2'>
                                        <ReasonsCard
                                            reasons={filteredReasons}
                                            selectedReasonCode={reason}
                                            onChange={this.handleReasonChange}
                                            labels={{
                                                ru: field.labelRu,
                                                kz: field.labelKz,
                                                en: field.labelEn,
                                            }}
                                        />
                                        {errors.reason && (
                                            <div className='vb-ms-3 vb-mt-1 vb-text-danger'>
                                                {t(
                                                    `form:reason:${errors.reason}`
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (field.key === 'reason' && !showCallReason) {
                                return null;
                            }
                            if (field.key === 'text') {
                                return <div className='vb-free-text' key={`text${index}`}>
                                    {this.getFieldLabel(field.key, {
                                        ru: field.labelRu,
                                        kz: field.labelKz,
                                        en: field.labelEn,
                                    })}</div>;
                            }

                            if (field.key === 'agreement') {
                                return (
                                    <AgreementField
                                        key={field.key + index}
                                        index={index}
                                        field={field}
                                        errors={errors}
                                        onInputChange={this.onInputChange}
                                        openFileViewer={openFileViewer}
                                    />
                                );
                            }
                            if (field.type !== 'text') {
                                return (
                                    <div
                                        className='vb-mb-2 vb-form-floating'
                                        key={field.key}
                                    >
                                        <InputMask
                                            mask={field.mask}
                                            pattern={field.pattern}
                                            autoComplete="off"
                                            type={
                                                field.key === 'phone'
                                                    ? 'phone'
                                                    : 'text'
                                            }
                                            className={`vb-form-control ${errors[field.key]
                                                ? 'vb-is-invalid'
                                                : ''}`}
                                            name={field.key}
                                            placeholder={t(
                                                `form:${field.key}:placeholder`,
                                            )}
                                            inputMode="numeric"
                                            value={
                                                field.settings &&
                                                    field.settings.value
                                                    ? field.settings.value
                                                    : ''
                                            }
                                            onChange={this.onInputChange}
                                            required={field.isRequired}
                                        />
                                        <label className='vb-form-label'>
                                            <span className='vb-floating-text'>
                                                {this.getFieldLabel(field.key, {
                                                    ru: field.labelRu,
                                                    kz: field.labelKz,
                                                    en: field.labelEn,
                                                })}
                                                {field.isRequired && '*'}
                                            </span>
                                        </label>
                                        {errors[field.key] && (
                                            <div className='vb-text-danger vb-ms-3 vb-mt-1'>
                                                {t(
                                                    `form:${field.key}:${errors[field.key]
                                                    }`
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    className='vb-mb-2 vb-form-floating'
                                    key={field.key}
                                >
                                    <input
                                        autoComplete="on"
                                        name={field.key}
                                        placeholder={t(
                                            `form:${field.key}:placeholder`
                                        )}
                                        value={
                                            field.settings &&
                                                field.settings.value
                                                ? field.settings.value
                                                : ''
                                        }
                                        inputMode="text"
                                        onChange={this.onInputChange}
                                        className={`vb-form-control ${errors[field.key] ? 'vb-is-invalid' : ''}`}
                                        required={field.isRequired}
                                    />
                                    <label className='vb-form-label'>
                                        <span className='vb-floating-text'>
                                            {this.getFieldLabel(field.key, {
                                                ru: field.labelRu,
                                                kz: field.labelKz,
                                                en: field.labelEn,
                                            })}
                                            {field.isRequired && '*'}
                                        </span>
                                    </label>
                                    {errors[field.key] && (
                                        <div className='vb-ms-3 vb-mt-1 vb-text-danger'>
                                            {t(
                                                `form:${field.key}:${errors[field.key]
                                                }`
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {showOperatorCode && (
                            <div className='vb-mb-2 vb-d-none'>
                                <label className='vb-form-label'>
                                    {t('form:operator:label')}
                                </label>
                                <InputMask
                                    mask="9999"
                                    autoComplete="on"
                                    type="text"
                                    className={'vb-form-control'}
                                    name="operator"
                                    value={operator}
                                    onChange={this.onInputChange}
                                />
                            </div>
                        )}
                        {
                            error && (
                                <div className='vb-ms-1 vb-text-danger'>
                                    {
                                        errorCode ? t(`generalErrors:${errorCode}`) : t('generalErrors:genericError')
                                    }
                                </div>
                            )
                        }
                    </form>
                </div>
                <div className='vb-pi-card__footer'>
                    <div className='vb-pi-card__footer-btn-contaner'>
                        {!this.forBcc && !this.forOtbasy && <div className='vb-agreement'>
                            {t('form:agreement:intro')}
                            <span className='vb-bcc-link'>{t('form:agreement:link-part')}</span>
                        </div>
                        }
                        {this.forOtbasy &&
                            <div className='vb-no-camera-instruction'>
                                <div className='vb-no-camera-instruction__inner'>
                                    <div className='vb-no-camera-instruction__qr'>
                                        <img src={qr} alt="QR для подключения" />
                                    </div>

                                    <div className='vb-no-camera-instruction__text'>
                                        <p className='vb-no-camera-instruction__title'>
                                            {t('qr-instructions:title')}
                                        </p>
                                        <ul className='vb-no-camera-instruction__list'>
                                            <li>{t('qr-instructions:step-1')}</li>
                                            <li>{t('qr-instructions:step-2')}</li>
                                            <li>{t('qr-instructions:step-3')}</li>
                                            <li>{t('qr-instructions:step-4')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        }
                        <button
                            onClick={this.registerCall}
                            className='vb-btn vb-btn-primary vb-text-white vb-px-4 vb-gradient-primary vb-btn-bcc vb-mt-4'
                        >
                            {t('action-label')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation('register-participant')(
    ParticipantInfoSourceForm
);
